/**
 * PerDocScanModal — compatibility wrapper over the canonical `CaptureModal`.
 *
 * Bill 2026-05-27 (slot-1 G1/T2): the start-gate ("Scan Now"/"Test Run") is
 * gone and there is now ONE capture modal. This file is a thin adapter that
 * preserves the original `onCapture` / `onApply(result)` / `onClose` prop
 * surface its two consumers rely on (PdwStage4DocsGrid + the slimmed
 * IndividualNraDocumentScanTabs), delegating all behaviour to
 * `capture/CaptureModal`:
 *
 *   - extraction auto-STARTS on capture/drop (no start gate) — D1
 *   - Apply STAYS (D1 + SPEC Screen 14)
 *   - two-tile Camera/Scanner | File-Upload chooser, drag-drop, PDF-on-save
 *
 * `initialFile` is a NEW optional prop: when a file is dropped on a tile the
 * parent hands it in here and the modal auto-starts on it (slot-1 G4/T6).
 *
 * `ScanResultPayload` is re-exported from CaptureModal so existing
 * `import { type ScanResultPayload } from './PerDocScanModal'` sites keep
 * compiling.
 */
import {
  CaptureModal,
  type ScanResultPayload,
  type CaptureArtifact,
  type OverrideOption,
} from './capture/CaptureModal';

export type { ScanResultPayload, CaptureArtifact, OverrideOption } from './capture/CaptureModal';

export interface PerDocScanModalProps {
  open: boolean;
  /** Doc-type label shown in the modal title (e.g. "Foreign Passport"). */
  docLabel: string;
  /** Hint shown under the title. */
  docHint?: string;
  /** Document-icon slug for the thumbnail fallback (DOCUMENT_ICONS key). */
  iconSlug?: string;
  /**
   * File capture handler — receives base64 + mime + optional chosenDocType
   * (set by the F254 Classify Picker when the FSR overrides). Returns scan
   * result. Bill 2026-05-28 — chosenDocType is the route-key for the
   * generic Primary / Secondary ID slots.
   */
  onCapture: (base64: string, mimeType: string, chosenDocType?: string) => Promise<ScanResultPayload>;
  /**
   * Apply handler — receives the scan result + the merged-PDF CaptureArtifact
   * (base64 PDF + originals + filename) + chosen doc-type override (when the
   * FSR refined via the Classify Picker). The artifact is REQUIRED for the C2
   * persist chain (PdwStage4DocsGrid → PdwShell → person.extra.attachedDocs).
   */
  onApply: (result: ScanResultPayload, artifact: CaptureArtifact, chosenDocType?: string) => void;
  /** Close handler — Cancel / × / Esc. */
  onClose: () => void;
  /**
   * Drag-drop hand-off (slot-1 G4/T6). When set, the chooser is skipped and
   * extraction auto-starts on this file.
   */
  initialFile?: File | null;
  /** Optional "Detected as …" override dropdown options. */
  overrideOptions?: ReadonlyArray<OverrideOption>;
  /** Optional 2-pass auto-classifier (suggests the detected type). */
  autoClassify?: (base64: string, mimeType: string) => Promise<{ docTypeValue: string; detectedRawType?: string }>;
  /** Attach-only doc type (no extraction pass) — shows the attach note incl. the military-ID restriction. */
  attachOnly?: boolean;
  /** Review import — reopen an earlier capture on its review step. */
  reviewOf?: { result: ScanResultPayload; artifact: CaptureArtifact; chosenDocType?: string } | null;
}

export function PerDocScanModal({
  open,
  docLabel,
  docHint,
  iconSlug,
  onCapture,
  onApply,
  onClose,
  initialFile,
  overrideOptions,
  autoClassify,
  attachOnly,
  reviewOf,
}: PerDocScanModalProps): JSX.Element | null {
  return (
    <CaptureModal
      open={open}
      docLabel={docLabel}
      {...(docHint ? { docHint } : {})}
      {...(iconSlug ? { iconSlug } : {})}
      {...(initialFile ? { initialFile } : {})}
      {...(overrideOptions ? { overrideOptions } : {})}
      {...(autoClassify ? { autoClassify } : {})}
      {...(attachOnly !== undefined ? { attachOnly } : {})}
      {...(reviewOf ? { reviewOf } : {})}
      onCapture={(base64, mimeType, chosenDocType) => onCapture(base64, mimeType, chosenDocType)}
      onApply={(result: ScanResultPayload, artifact: CaptureArtifact, chosenDocType?: string) => onApply(result, artifact, chosenDocType)}
      onClose={onClose}
    />
  );
}
